import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, Clock, Eye, ArrowLeft, Share2, User } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentPosts, setRecentPosts] = useState([]);

  useEffect(() => {
    // Fetch the blog post
    fetch(`${API_URL}/api/blog/${slug}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => setPost(data))
      .catch(err => console.error('Error fetching post:', err))
      .finally(() => setLoading(false));

    // Fetch recent posts for sidebar
    fetch(`${API_URL}/api/blog/posts?limit=5`)
      .then(r => r.json())
      .then(data => setRecentPosts(data.posts || []))
      .catch(() => {});
  }, [slug]);

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return 'Recent';
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ 
        title: post.title, 
        url: window.location.href 
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5c0701]"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Article Not Found</h2>
        <p className="text-gray-600 mb-6">The article you're looking for doesn't exist or has been removed.</p>
        <Link to="/blog" className="text-[#3a87fe] hover:underline flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Back to CT Remodel News
        </Link>
      </div>
    );
  }

  const readingTime = post.reading_time || Math.max(1, Math.round((post.word_count || 1000) / 250));
  const wordCount = post.word_count || 1000;
  const postUrl = `https://cthomeremodels.com/blog/${post.slug}`;
  const ogImageUrl = `https://cthomeremodels.com/og/v2/blog.png`;

  // Schema stacking: Article + FAQ + BreadcrumbList + Organization
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "mainEntityOfPage": { "@type": "WebPage", "@id": postUrl },
    "headline": post.title,
    "description": post.meta_description || post.title,
    "datePublished": post.created_at,
    "dateModified": post.updated_at || post.created_at,
    "wordCount": wordCount,
    "author": {
      "@type": "Organization",
      "name": "Visser General Construction",
      "url": "https://cthomeremodels.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Visser General Construction LLC",
      "url": "https://cthomeremodels.com",
      "logo": { "@type": "ImageObject", "url": "https://cthomeremodels.com/favicon.ico" },
      "contactPoint": { "@type": "ContactPoint", "telephone": "+1-203-604-3202", "contactType": "customer service", "areaServed": "US-CT" }
    },
    "articleSection": post.category || "Home Remodeling",
    "keywords": (post.tags || []).join(", "),
    "inLanguage": "en-US"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://cthomeremodels.com" },
      { "@type": "ListItem", "position": 2, "name": "CT Remodel News", "item": "https://cthomeremodels.com/blog" },
      { "@type": "ListItem", "position": 3, "name": post.title, "item": postUrl }
    ]
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "HomeAndConstructionBusiness",
    "name": "Visser General Construction LLC",
    "url": "https://cthomeremodels.com",
    "telephone": "+1-203-604-3202",
    "address": { "@type": "PostalAddress", "streetAddress": "42 Gay St", "addressLocality": "Meriden", "addressRegion": "CT", "postalCode": "06450", "addressCountry": "US" },
    "areaServed": { "@type": "State", "name": "Connecticut" },
    "priceRange": "$$",
    "hasCredential": { "@type": "EducationalOccupationalCredential", "credentialCategory": "Home Improvement Contractor License", "recognizedBy": { "@type": "Organization", "name": "State of Connecticut" } }
  };

  // FAQ schema from post data
  const faqSchema = post.faqs && post.faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": post.faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
    }))
  } : null;

  // Build all schemas into a single array for clean rendering
  const schemas = [articleSchema, breadcrumbSchema, orgSchema];
  if (faqSchema) schemas.push(faqSchema);

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{`${post.title} | CT Remodel News`}</title>
        <meta name="description" content={post.meta_description || post.title} />
        <link rel="canonical" href={postUrl} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.meta_description || post.title} />
        <meta property="og:url" content={postUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="article:published_time" content={post.created_at} />
        <meta property="article:section" content={post.category || 'Home Remodeling'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={ogImageUrl} />
      </Helmet>
      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      ))}
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link to="/blog" className="text-[#3a87fe] hover:text-[#2968d9] flex items-center gap-2 text-sm">
            <ArrowLeft className="w-4 h-4" /> Back to CT Remodel News
          </Link>
        </div>
      </div>

      {/* Article */}
      <article className="max-w-4xl mx-auto px-4 py-10">
        <div className="bg-white rounded-xl shadow-sm p-6 md:p-10">
          {/* Meta info */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-6">
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {formatDate(post.created_at)}
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {readingTime} min read
            </span>
            <span className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              {post.views || 0} views
            </span>
            <button 
              onClick={handleShare}
              className="ml-auto flex items-center gap-2 text-gray-500 hover:text-[#3a87fe] transition"
            >
              <Share2 className="w-4 h-4" /> Share
            </button>
          </div>

          {/* Title */}
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 leading-tight">
            {post.title}
          </h1>

          {/* Category & Author */}
          <div className="flex items-center gap-4 mb-8 pb-6 border-b">
            <span className="bg-[#5c0701] text-white px-3 py-1 rounded-full text-xs font-medium">
              {post.category || 'Home Remodeling'}
            </span>
            <span className="flex items-center gap-2 text-sm text-gray-600">
              <User className="w-4 h-4" />
              {post.author || 'Visser General Construction'}
            </span>
          </div>

          {/* Content */}
          <div 
            className="prose prose-lg max-w-none
              prose-headings:text-gray-900 prose-headings:font-bold
              prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-4
              prose-h3:text-lg prose-h3:mt-6 prose-h3:mb-3
              prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4
              prose-ul:my-4 prose-li:text-gray-700
              prose-a:text-[#3a87fe] prose-a:no-underline hover:prose-a:underline
              prose-strong:text-gray-900"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-8 pt-6 border-t">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Related Topics:</h4>
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag, i) => (
                  <span key={i} className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-10 bg-gradient-to-r from-[#5c0701]/5 to-[#3a87fe]/5 rounded-xl p-6 border border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Ready to Transform Your Home?</h3>
            <p className="text-gray-600 mb-4">
              Get a free estimate from Visser General Construction. We serve Meriden, Wallingford, Cheshire, 
              and communities throughout Central Connecticut.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link 
                to="/contact" 
                className="bg-[#5c0701] text-white px-6 py-3 rounded-lg font-semibold text-center hover:bg-[#7a0901] transition"
              >
                Get Free Estimate
              </Link>
              <a 
                href="tel:2036043202" 
                className="border border-[#5c0701] text-[#5c0701] px-6 py-3 rounded-lg font-semibold text-center hover:bg-[#5c0701]/5 transition"
              >
                Call (203) 604-3202
              </a>
            </div>
          </div>
        </div>

        {/* Recent Articles */}
        {recentPosts.length > 0 && (
          <div className="mt-12">
            <h2 className="text-xl font-bold text-gray-900 mb-6">More Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {recentPosts
                .filter(p => p.slug !== slug)
                .slice(0, 4)
                .map((rp, index) => (
                  <div key={rp.id || rp.slug} className="bg-white rounded-lg shadow-sm p-5 hover:shadow-md transition">
                    <span className="text-xs text-gray-500">{formatDate(rp.created_at)}</span>
                    <h3 className="font-semibold text-gray-900 mt-2 mb-2 line-clamp-2">
                      <Link to={`/blog/${rp.slug}`} className="hover:text-[#3a87fe] transition">
                        {rp.title}
                      </Link>
                    </h3>
                    <Link to={`/blog/${rp.slug}`} className="text-sm text-[#3a87fe] hover:underline">
                      Read more
                    </Link>
                  </div>
                ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
};

export default BlogPost;
